import type { DevelopmentTask } from '../../app/development-tasks'
import { useAccountMembership, useRequestDevelopmentTaskPreview } from '../../app/queries'
import { Button } from '../../components/button'
import { Card, CardHint, CardTitle } from '../../components/card'
import { Tag } from '../../components/tag'
import { t } from '../../copy'
import {
  previewBlameCopy,
  previewBlockedCopy,
  previewClosed,
  releasedStates,
  stateCopy,
  stateTone,
} from './preview-lane-copy'

/**
 * 每条任务各算各的。
 *
 * 路由从一条任务跳到另一条时不会把这棵树重建，而「交一次预览」那个 mutation
 * 的状态不会自己清掉——上一条任务的回执会原样显示在下一条的面板上，
 * 说着一个跟这条任务毫无关系的版本。`key` 让换任务等于换一块面板。
 *
 * 刻意不写成「调用方记得传 key」：那是一条写在注释里的约定，
 * 而这个仓库为「写在注释里的约定」付过好几次代价。
 */
export function PreviewLanePanel({ task }: { task: DevelopmentTask }) {
  return <PreviewLane key={task.id} task={task} />
}

function PreviewLane({ task }: { task: DevelopmentTask }) {
  const preview = useRequestDevelopmentTaskPreview(task.project_id, task.id)
  const account = useAccountMembership()
  const receipt = preview.data
  // 预览只跑 AI 已经交出来的那一版。attempt 还没交结果时这里是空的——
  // 那时候不是「失败」，是还没到时候，所以按钮停用并说清在等什么。
  const ownerTip = task.current_attempt?.result_sha ?? ''
  // 这条路由要 operate（服务端 register 的第二个参数）。只能看的人点下去必然被拒，
  // 那就别给他一个点了才知道不行的按钮。还没读到能力时按不能点算——
  // 反过来会让按钮闪一下变灰，而那一下正好够人点下去。
  const blocked = previewBlockedCopy({
    canOperate: account.data?.capabilities.includes('operate') ?? false,
    closed: previewClosed(task.state),
    hasTip: Boolean(ownerTip),
  })
  const state = receipt?.state
  const released = state ? releasedStates[state] : false
  // 回执认的是它锁定的那一版。AI 后来又交了新的时，那张回执讲的是上一版的事——
  // 这时候按钮点下去交的是新的那一版，标签就不能再说「再跑一遍这一版」。
  const receiptIsTip = receipt?.sha === ownerTip

  return (
    <Card data-testid="development-task-preview">
      <CardTitle>{t('development_task.preview.panel.title')}</CardTitle>
      <CardHint className="mt-npc-gap-sm leading-[1.6]">{t('development_task.preview.panel.hint')}</CardHint>
      {state ? (
        <div className="mt-npc-card">
          <Tag tone={stateTone[state]} data-testid="development-task-preview-state">{t(stateCopy[state])}</Tag>
        </div>
      ) : null}
      <dl className="mt-npc-card mb-0 grid grid-cols-[minmax(96px,0.5fr)_minmax(0,1fr)] gap-x-npc-gap-lg gap-y-npc-gap-sm text-npc-caption">
        <Row label={t('development_task.preview.field.owner_tip')} value={ownerTip} testId="development-task-preview-owner-tip" />
        {receipt ? (
          <Row
            label={released ? t('development_task.preview.field.release_sha') : t('development_task.preview.field.preview_sha')}
            value={receipt.sha}
            testId={released ? 'development-task-preview-release-sha' : 'development-task-preview-sha'}
          />
        ) : null}
      </dl>
      {state === 'preview_failed_rolled_back' ? (
        <p className="mt-npc-gap-lg m-0 text-npc-caption text-npc-ink-medium" data-testid="development-task-preview-rollback">
          {t('development_task.preview.rollback')}
        </p>
      ) : null}
      <Button
        variant="secondary"
        className="mt-npc-card"
        disabled={blocked !== null || preview.isPending}
        onClick={() => preview.mutate(ownerTip)}
      >
        {preview.isPending
          ? t('development_task.preview.pending')
          : receiptIsTip
            ? t('development_task.preview.again')
            : t('development_task.preview.action')}
      </Button>
      {blocked ? (
        <p className="mt-npc-gap-lg m-0 text-npc-caption text-npc-ink-soft" data-testid="development-task-preview-hint">
          {t(blocked)}
        </p>
      ) : null}
      {preview.error ? (
        <div className="mt-npc-gap-lg" data-testid="development-task-preview-blame">
          <p role="alert" className="m-0 border-l-2 border-npc-red bg-npc-red-bg px-npc-grid-sm py-npc-gap-sm text-npc-caption text-npc-red-ink">
            {preview.error instanceof Error ? preview.error.message : t('development_task.preview.blame.unknown')}
          </p>
          <p className="mt-npc-gap-sm m-0 text-npc-caption text-npc-ink-medium" data-testid="development-task-preview-blame-domain">
            <span className="text-npc-ink-softer">{t('development_task.preview.blame.label')}</span>
            {' '}
            {t(previewBlameCopy(preview.error))}
          </p>
        </div>
      ) : null}
    </Card>
  )
}

function Row({ label, value, testId }: { label: string; value: string; testId: string }) {
  if (!value) return null
  return (
    <>
      <dt className="text-npc-ink-softer">{label}</dt>
      <dd className="m-0 break-all font-npc-mono text-npc-ink-medium" data-testid={testId}>{value}</dd>
    </>
  )
}
